import { useEffect, useState, type ReactElement } from 'react';
import { Link, NavLink, Outlet, useParams } from 'react-router-dom';
import { useI18n } from '../../lib/i18n/I18nProvider';
import { newIntent, useApiAction, useApiList, useApiResource } from '../../lib/api/queries';
import type { Expo, ExpoLayoutState, ExpoSession, SlaConfig } from '../../lib/api/types';
import { Badge, Card, DataTable, Field, Ltr, PageTitle, Select, TextArea } from '../../ui/primitives';
import { DegradedState, EmptyState, ErrorState, LoadingState, StateIcon } from '../../ui/states';
import { ApprovalHandoff, AuditStatement, RefusalWithRemedy, StepUpPanel } from '../../ui/guards';
import { useSession } from '../../lib/auth/session';
import type { HandoffGrant, HandoffRequest } from '../../lib/auth/handoff';
import type { StringKey } from '../../lib/i18n/strings';
import { expoStatusKey, statusTone } from './Expos';
import { ApiError } from '../../lib/api/problem';
import { isoToZonedLocal } from '../../lib/datetime';
import {
  ExpoScheduleFields,
  schedulePayload,
  validateSchedule,
  type ScheduleValues,
} from './ExpoScheduleFields';

const TRANSITIONS = [
  'announced',
  'registration_open',
  'registration_closed',
  'live',
  'ended',
  'archived',
  'cancelled',
];
const EXPO_STATUS_KEYS_FOR_DETAIL: Record<string, StringKey> = {
  announced: 'expo.status.announced',
  registration_open: 'expo.status.registration_open',
  registration_closed: 'expo.status.registration_closed',
  live: 'expo.status.live',
  ended: 'expo.status.ended',
  archived: 'expo.status.archived',
  cancelled: 'expo.status.cancelled',
};

/** Only `live` requires step-up AND four eyes (tenancy.ExpoTransitionNeedsStepUp). */
const NEEDS_STEP_UP = new Set(['live']);
/** `cancelled` is terminal, so the operator types the expo slug (design/p13-dashboards.md §7). */
const NEEDS_TYPED_CONFIRMATION = new Set(['cancelled']);

const REASON_CODES = ['operational', 'commercial', 'compliance', 'security', 'administrative_error'];
const REASON_KEYS: Record<string, StringKey> = {
  operational: 'reason.operational',
  commercial: 'reason.commercial',
  compliance: 'reason.compliance',
  security: 'reason.security',
  administrative_error: 'reason.administrative_error',
};

/** The permission `expo:transition → live` is step-up and four-eyes gated on. */
const GO_LIVE = 'expo.go_live';
/** doc 07 §5.1 marks the recording policy "(step-up)"; the handler names this action. */
const RECORDING_POLICY = 'expo.recording_policy.manage';

const RECORDING_POLICIES = ['disabled', 'best_effort', 'required'];
const RECORDING_POLICY_KEYS: Record<string, StringKey> = {
  disabled: 'recording.disabled',
  best_effort: 'recording.best_effort',
  required: 'recording.required',
};
/** The one `SetRecordingPolicy` refuses outright until decision gate G6 closes (doc 06 §3.3). */
const GATED_POLICY = 'required';

export function AdminExpoLayout(): ReactElement {
  const { expoId = '' } = useParams();
  const { t, bilingual } = useI18n();
  const expo = useApiResource<Expo>(expoId ? `/v1/admin/expos/${expoId}` : null);

  if (expo.isLoading) return <LoadingState rows={5} />;
  if (expo.error) return <ErrorState error={expo.error} onRetry={() => void expo.refetch()} />;
  if (!expo.data) return <EmptyState />;

  const e = expo.data.data;
  const statusKey = expoStatusKey(e.status);
  return (
    <>
      <PageTitle title={bilingual(e.name_ar, e.name_en)}>
        <Ltr>
          {e.slug} · {e.timezone}
        </Ltr>
      </PageTitle>
      <div className="shell__toolbar">
        <Badge tone={statusTone(e.status)}>{statusKey ? t(statusKey) : e.status}</Badge>
        <nav className="row" aria-label={t('admin.expos.title')}>
          <NavLink className="btn btn--secondary btn--small" to={`/admin/expos/${expoId}`} end>
            {t('admin.expo.lifecycle')}
          </NavLink>
          <NavLink className="btn btn--secondary btn--small" to={`/admin/expos/${expoId}/floor`}>
            {t('admin.slots.title')}
          </NavLink>
          <NavLink className="btn btn--secondary btn--small" to={`/admin/expos/${expoId}/participations`}>
            {t('admin.expo.participations')}
          </NavLink>
          <NavLink className="btn btn--secondary btn--small" to={`/admin/expos/${expoId}/releases`}>
            {t('admin.expo.releases')}
          </NavLink>
          <NavLink className="btn btn--secondary btn--small" to={`/admin/expos/${expoId}/sponsors`}>
            {t('admin.expo.sponsors')}
          </NavLink>
        </nav>
      </div>
      <Outlet />
    </>
  );
}

/** `ADM-03` lifecycle · `ADM-04` sessions · `ADM-12` recording policy · `ADM-40` SLA. */
export function AdminExpoLifecycle(): ReactElement {
  const { expoId = '' } = useParams();
  const { t } = useI18n();
  const session = useSession();
  const expo = useApiResource<Expo>(expoId ? `/v1/admin/expos/${expoId}` : null);
  const transition = useApiAction<Expo>([`/v1/admin/expos/${expoId}`, '/v1/admin/expos']);

  const [to, setTo] = useState(TRANSITIONS[0] ?? 'announced');
  const [reason, setReason] = useState(REASON_CODES[0] ?? 'operational');
  const [note, setNote] = useState('');
  const [typed, setTyped] = useState('');
  const [grant, setGrant] = useState<HandoffGrant | null>(null);

  const e = expo.data?.data;
  const version = expo.data?.version ?? e?.version ?? null;
  const needsTyped = NEEDS_TYPED_CONFIRMATION.has(to);
  const needsGuard = NEEDS_STEP_UP.has(to);
  const typedOk = !needsTyped || typed.trim() === (e?.slug ?? '');
  const stepUp = session.grantFor(GO_LIVE);
  const toLabel = t(EXPO_STATUS_KEYS_FOR_DETAIL[to] ?? 'expo.status.announced');
  const reasonLabel = (value: string): string => t(REASON_KEYS[value] ?? 'reason.operational');

  const handoff: HandoffRequest | null =
    version === null
      ? null
      : {
          action: GO_LIVE,
          aggregateType: 'expo',
          aggregateId: expoId,
          expectedVersion: version,
          reasonCode: reason,
          label: `transition ${e?.slug ?? expoId} to ${to}`,
        };

  /**
   * The intent is formed from the state AT THE MOMENT OF THE CLICK and never re-read.
   *
   * A retry used to call this function again, which read `to` and `typed` afresh: change the
   * dropdown after a refusal and "retry" performed a different transition, and a typed
   * confirmation given for `cancelled` could be carried into whatever was selected next. What is
   * re-sent now is the frozen intent, with its own key, version and confirmation.
   */
  const run = (): void => {
    if (version === null) return;
    transition.mutate(newIntent({
      path: `/v1/admin/expos/${expoId}:transition`,
      body: { to, reason_code: reason, note, force: false },
      ifMatch: version,
      label: `transition to ${to}`,
      ...(needsGuard ? { action: GO_LIVE } : {}),
      ...(needsTyped ? { confirm: { expected: e?.slug ?? '', typed } } : {}),
    }));
  };

  return (
    <>
      <ExpoScheduleCard expoId={expoId} />
      <ActiveHallCard expoId={expoId} />
      <Card title={t('admin.expo.lifecycle')} description={t('admin.expo.lifecycle.desc')}>
        <AuditStatement
          summary={
            <>
              <p>{t('admin.expo.transition.notice', { status: toLabel })}</p>
              {NEEDS_STEP_UP.has(to) ? <p>{t('admin.releases.activate.desc')}</p> : null}
            </>
          }
        />

        <Select
          label={t('admin.expo.transition')}
          name="to"
          value={to}
          options={TRANSITIONS.map((s) => ({
            value: s,
            label: t(EXPO_STATUS_KEYS_FOR_DETAIL[s] ?? 'expo.status.announced'),
          }))}
          onChange={setTo}
        />
        <Select
          label={t('guard.reason')}
          name="reason_code"
          value={reason}
          options={REASON_CODES.map((s) => ({ value: s, label: reasonLabel(s) }))}
          onChange={setReason}
        />
        <TextArea label={t('guard.note')} name="note" value={note} onChange={setNote} />
        {needsTyped ? (
          <Field
            label={t('guard.typed.label', { value: e?.slug ?? '' })}
            name="typed"
            value={typed}
            onChange={setTyped}
            ltr
            {...(typed && !typedOk ? { error: t('guard.typed.mismatch') } : {})}
          />
        ) : null}

        {needsGuard && e ? (
          <>
            {stepUp ? null : <StepUpPanel action={GO_LIVE} />}
            <ApprovalHandoff request={handoff} onGranted={setGrant} />
          </>
        ) : null}

        {transition.error instanceof ApiError && transition.error.code === 'expo.transition_guard_failed' ? (
          <DegradedState title={t('admin.expo.transition.notReady')}>
            {to === 'registration_open'
              ? t('admin.expo.transition.registrationRemedy')
              : to === 'live'
                ? t('admin.expo.transition.liveRemedy')
                : t('admin.expo.transition.scheduleRemedy')}
          </DegradedState>
        ) : null}
        {transition.error ? (
          <RefusalWithRemedy
            error={transition.error}
            action={GO_LIVE}
            retryExact={transition.retryLast}
            onReread={() => {
              setGrant(null);
              void expo.refetch();
            }}
          />
        ) : null}
        <button
          className="btn"
          type="button"
          disabled={transition.isPending || !typedOk || (needsGuard && !grant)}
          onClick={run}
        >
          {t('admin.expo.transition')}
        </button>
      </Card>

      <ExpoSessions expoId={expoId} />
      <RecordingPolicy expoId={expoId} />
      <SlaCard expoId={expoId} />
    </>
  );
}

/**
 * WHICH HALL THIS EXPO IS SERVING, ON THE SCREEN THAT DECIDES ITS LIFECYCLE.
 *
 * Going live with no active hall is the failure this card exists to prevent: the manifest names an
 * `expo.hall_layout_id`, and an expo that points at nothing renders an empty world for every
 * visitor who walks in. There are three states worth distinguishing and the card distinguishes
 * them — no layout published at all; a layout published AND attached (`active`); and a layout
 * published but NOT attached, which looks like success everywhere else and is not.
 */
function ActiveHallCard({ expoId }: { expoId: string }): ReactElement {
  const { t, formatDateTime } = useI18n();
  const layout = useApiResource<ExpoLayoutState & { version?: number }>(`/v1/admin/expos/${expoId}/layout`);
  const hall = layout.data?.data.current_hall ?? null;
  const floorHref = `/admin/expos/${expoId}/floor`;

  return (
    <Card title={t('admin.currentHall.activeTitle')} description={t('admin.currentHall.desc')} testId="active-hall">
      {layout.isLoading ? <LoadingState rows={2} /> : null}
      {layout.error ? <ErrorState error={layout.error} onRetry={() => void layout.refetch()} /> : null}
      {hall ? (
        <>
          <p className="row">
            <Badge tone={hall.active ? 'ok' : 'warn'}>
              {hall.active ? t('admin.currentHall.active') : t('admin.currentHall.inactive')}
            </Badge>
            <span>
              {t('admin.currentHall.publishedLayout', { version: hall.layout_version })}
            </span>
          </p>
          <dl className="kv">
            <dt>{t('admin.currentHall.sceneKey')}</dt>
            <dd>
              <Ltr>
                {hall.scene_key} · v{hall.scene_version}
              </Ltr>
            </dd>
            <dt>{t('admin.layouts.publishedAt')}</dt>
            <dd>{formatDateTime(hall.published_at)}</dd>
          </dl>
          {hall.active ? null : (
            <p className="state state--warning" role="status">{t('admin.currentHall.inactiveHint')}</p>
          )}
        </>
      ) : layout.data ? (
        <EmptyState title={t('admin.currentHall.none')}>{t('admin.currentHall.noneHint')}</EmptyState>
      ) : null}
      <p>
        <Link className="btn btn--secondary btn--small" to={floorHref}>
          {t('admin.currentHall.openFloor')}
        </Link>
      </p>
    </Card>
  );
}

function ExpoScheduleCard({ expoId }: { expoId: string }): ReactElement {
  const { t } = useI18n();
  const expo = useApiResource<Expo>(`/v1/admin/expos/${expoId}`);
  const save = useApiAction<Expo>([`/v1/admin/expos/${expoId}`, '/v1/admin/expos']);
  const [values, setValues] = useState<ScheduleValues>({
    registrationOpens: '', eventStarts: '', eventEnds: '', automatic: false,
  });
  const [attempted, setAttempted] = useState(false);
  const [expectedBooths, setExpectedBooths] = useState('0');

  useEffect(() => {
    const current = expo.data?.data;
    if (!current) return;
    setValues({
      registrationOpens: isoToZonedLocal(current.registration_opens_at, current.timezone),
      eventStarts: isoToZonedLocal(current.go_live_scheduled_at, current.timezone),
      eventEnds: isoToZonedLocal(current.event_ends_at, current.timezone),
      automatic: current.auto_transition_enabled,
    });
    setExpectedBooths(String(current.expected_booth_count));
  }, [expo.data]);

  if (expo.isLoading) return <LoadingState rows={2} />;
  if (expo.error) return <ErrorState error={expo.error} onRetry={() => void expo.refetch()} />;
  if (!expo.data) return <EmptyState />;

  const current = expo.data.data;
  const version = expo.data.version ?? current.version;
  const errors = validateSchedule(values, current.timezone);
  const hasErrors = Object.keys(errors).length > 0;

  return (
    <Card title={t('admin.expo.schedule.title')} description={t('admin.expo.schedule.editHint')}>
      <form
        className="expo-schedule-form"
        onSubmit={(event) => {
          event.preventDefault();
          setAttempted(true);
          const payload = schedulePayload(values, current.timezone);
          if (hasErrors || !payload) return;
          save.mutate(newIntent({
            path: `/v1/admin/expos/${expoId}`,
            method: 'PATCH',
            body: { ...payload, expected_booth_count: Number(expectedBooths) },
            ifMatch: version,
            label: 'update expo schedule',
          }));
        }}
      >
        {attempted && hasErrors ? (
          <div className="state state--error" role="alert">{t('admin.expo.schedule.error.summary')}</div>
        ) : null}
        <ExpoScheduleFields
          values={values}
          onChange={setValues}
          timezone={current.timezone}
          errors={attempted ? errors : {}}
        />
        <div className="expo-layout-metrics">
          <Field label={t('admin.expos.expectedBooths')} hint={t('admin.expos.expectedBoothsHint')}
            name="expected_booth_count" type="number" value={expectedBooths} onChange={setExpectedBooths} required ltr />
          <div><span>{t('admin.layoutEngine.actual')}</span><strong>{current.actual_booth_count}</strong></div>
        </div>
        {save.error ? <ErrorState error={save.error} onRetry={save.retryLast} /> : null}
        <button className="btn" type="submit" disabled={save.isPending}>
          {t('action.save')}
        </button>
      </form>
    </Card>
  );
}

function ExpoSessions({ expoId }: { expoId: string }): ReactElement {
  const { t, formatDateTime, bilingual } = useI18n();
  const path = `/v1/admin/expos/${expoId}/sessions`;
  const sessions = useApiList<ExpoSession>(path);
  const create = useApiAction<ExpoSession>([path]);

  const [labelAr, setLabelAr] = useState('');
  const [labelEn, setLabelEn] = useState('');
  const [starts, setStarts] = useState('');
  const [ends, setEnds] = useState('');

  return (
    <Card title={t('admin.expo.sessions')} description={t('admin.expo.sessions.desc')}>
      {sessions.isLoading ? <LoadingState /> : null}
      {sessions.error ? <ErrorState error={sessions.error} onRetry={() => void sessions.refetch()} /> : null}
      {sessions.data ? (
        <DataTable<ExpoSession>
          caption={t('admin.expo.sessions')}
          rows={sessions.data.items ?? []}
          rowKey={(s) => s.expo_session_id}
          empty={<EmptyState>{t('admin.expo.sessions.desc')}</EmptyState>}
          columns={[
            { key: 'seq', header: '#', cell: (s) => <span className="numeral">{s.sequence}</span> },
            { key: 'label', header: t('table.name'), cell: (s) => bilingual(s.label_ar, s.label_en) },
            { key: 'starts', header: t('admin.expo.sessions.starts'), cell: (s) => formatDateTime(s.starts_at) },
            { key: 'ends', header: t('admin.expo.sessions.ends'), cell: (s) => formatDateTime(s.ends_at) },
            {
              key: 'status',
              header: t('table.status'),
              cell: (s) => <Badge tone={statusTone(s.status)}>{s.status}</Badge>,
            },
          ]}
        />
      ) : null}

      <form
        onSubmit={(ev) => {
          ev.preventDefault();
          create.mutate(newIntent({
            path,
            body: {
              label_ar: labelAr,
              label_en: labelEn,
              starts_at: starts ? new Date(starts).toISOString() : '',
              ends_at: ends ? new Date(ends).toISOString() : '',
            },
          }));
        }}
      >
        <Field label={t('admin.expos.nameAr')} name="label_ar" value={labelAr} onChange={setLabelAr} required />
        <Field label={t('admin.expos.nameEn')} name="label_en" value={labelEn} onChange={setLabelEn} required ltr />
        <Field
          label={t('admin.expo.sessions.starts')}
          name="starts_at"
          type="datetime-local"
          value={starts}
          onChange={setStarts}
          required
          ltr
        />
        <Field
          label={t('admin.expo.sessions.ends')}
          name="ends_at"
          type="datetime-local"
          value={ends}
          onChange={setEnds}
          required
          ltr
        />
        {create.error ? <ErrorState error={create.error} /> : null}
        <button className="btn" type="submit" disabled={create.isPending}>
          {t('action.create')}
        </button>
      </form>
    </Card>
  );
}

/**
 * `ADM-12`. A recording-policy change is STEP-UP **AND** FOUR EYES, and this screen offers both.
 *
 * It used to offer only the step-up, which made the card a dead control: `tenancy.SetRecordingPolicy`
 * calls `ConsumeApprovalTx` before it touches the row, so with no approval in existence every save
 * ended in `auth.four_eyes_required` and the screen had nothing to say about it. The hand-off is the
 * same one the go-live uses — aggregate `expo`, at the expo's current version, no proposal, because
 * this act is a verb applied to a version rather than a payload.
 */
function RecordingPolicy({ expoId }: { expoId: string }): ReactElement {
  const { t } = useI18n();
  const session = useSession();
  const expo = useApiResource<Expo>(`/v1/admin/expos/${expoId}`);
  const save = useApiAction<Expo>([`/v1/admin/expos/${expoId}`]);
  const [policy, setPolicy] = useState('best_effort');
  const [justification, setJustification] = useState('');
  const [notice, setNotice] = useState('');
  const [grant, setGrant] = useState<HandoffGrant | null>(null);

  useEffect(() => {
    if (expo.data) setPolicy(expo.data.data.recording_policy);
  }, [expo.data]);

  const version = expo.data?.version ?? expo.data?.data.version ?? null;
  const stepUp = session.grantFor(RECORDING_POLICY);
  const policyLabel = (value: string): string => t(RECORDING_POLICY_KEYS[value] ?? 'recording.best_effort');
  const handoff: HandoffRequest | null =
    version === null
      ? null
      : {
          action: RECORDING_POLICY,
          aggregateType: 'expo',
          aggregateId: expoId,
          expectedVersion: version,
          reasonCode: 'compliance',
          label: `recording policy → ${policy}`,
        };
  const run = (): void => {
    if (version === null || !grant) return;
    save.mutate(newIntent({
      path: `/v1/admin/expos/${expoId}/recording-policy`,
      method: 'PATCH',
      // The decoder is strict and the field is `notice_version` — `recording_notice_version` was an
      // unknown field, so every save was refused before it reached the domain.
      body: { recording_policy: policy, notice_version: notice, justification },
      ifMatch: version,
      action: RECORDING_POLICY,
      label: `recording policy → ${policy}`,
    }));
  };

  return (
    <Card
      title={t('admin.expo.recordingPolicy')}
      description={t('admin.expo.recordingPolicy.desc')}
      testId="recording-policy"
    >
      <AuditStatement summary={<p>{t('admin.expo.recordingPolicy.notice', { policy: policyLabel(policy) })}</p>} />
      {/* `required` IS GATED, AND THE SCREEN SAYS SO RATHER THAN OFFERING IT PLAINLY.
          `tenancy.SetRecordingPolicy` answers 422 `expo.recording_policy_gated` for it —
          "blocked until decision gate G6 (recorded counsel and DPO approval) closes; doc 06 §3.3" —
          BEFORE any credential is looked at. An option that cannot succeed, sitting in a list beside
          two that can, spends an operator's step-up and a colleague's approval on a refusal that
          was decided before either was raised. It stays in the list because it is a real value of
          the column and the gate is expected to close; what it does not do is look available. */}
      <Select
        label={t('admin.expo.recordingPolicy')}
        name="recording_policy"
        value={policy}
        options={RECORDING_POLICIES.map((s) => ({
          value: s,
          label:
            s === GATED_POLICY
              ? `${policyLabel(s)} — ${t('admin.expo.recordingPolicy.gated.short')}`
              : policyLabel(s),
        }))}
        onChange={setPolicy}
      />
      {policy === GATED_POLICY ? (
        <DegradedState title={t('admin.expo.recordingPolicy.gated.short')}>
          <p>{t('admin.expo.recordingPolicy.gated')}</p>
        </DegradedState>
      ) : null}
      <Field label={t('recording.noticeVersion')} name="notice" value={notice} onChange={setNotice} ltr required />
      <TextArea
        label={t('recording.justification')}
        name="justification"
        value={justification}
        onChange={setJustification}
        required
      />
      {stepUp ? null : <StepUpPanel action={RECORDING_POLICY} />}
      <ApprovalHandoff request={handoff} onGranted={setGrant} />
      {save.error ? (
        <RefusalWithRemedy
          error={save.error}
          action={RECORDING_POLICY}
          retryExact={save.retryLast}
          onReread={() => {
            setGrant(null);
            void expo.refetch();
          }}
        />
      ) : null}
      <button
        className="btn"
        type="button"
        disabled={save.isPending || version === null || !grant || policy === GATED_POLICY}
        onClick={run}
      >
        {t('action.save')}
      </button>
    </Card>
  );
}

const SLA_FIELDS: { key: keyof SlaConfig; label: StringKey }[] = [
  { key: 'call_accept_timeout_s', label: 'sla.callAcceptTimeout' },
  { key: 'missed_call_breach_pct', label: 'sla.missedCallLimit' },
  { key: 'unstaffed_grace_s', label: 'sla.unstaffedGrace' },
  { key: 'seat_hold_ttl_s', label: 'sla.seatHoldDuration' },
  { key: 'queue_offer_window_s', label: 'sla.queueOfferWindow' },
  { key: 'queue_reoffer_limit', label: 'sla.queueReofferLimit' },
  { key: 'close_grace_s', label: 'sla.closeGrace' },
];

function SlaCard({ expoId }: { expoId: string }): ReactElement {
  const { t } = useI18n();
  const path = `/v1/admin/expos/${expoId}/sla-config`;
  const sla = useApiResource<SlaConfig>(path);
  const save = useApiAction<SlaConfig>([path]);
  const [draft, setDraft] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!sla.data) return;
    const next: Record<string, string> = {};
    for (const f of SLA_FIELDS) next[f.key] = String(sla.data.data[f.key]);
    setDraft(next);
  }, [sla.data]);

  const version = sla.data?.version ?? sla.data?.data.version ?? null;
  const run = (): void => {
    if (version === null) return;
    const body: Record<string, number> = {};
    for (const f of SLA_FIELDS) body[f.key] = Number(draft[f.key] ?? 0);
    save.mutate(newIntent({ path, method: 'PUT', body, ifMatch: version }));
  };

  return (
    <Card title={t('admin.expo.sla')}>
      {/* The obligation statement sits IMMEDIATELY above the controls (design/p13-dashboards.md ADM-40). */}
      <div className="state state--locked">
        <p className="state__title">
          <StateIcon kind="locked" />
          100 %
        </p>
        <p>{t('admin.expo.sla.obligation')}</p>
      </div>
      {sla.isLoading ? <LoadingState /> : null}
      {sla.error ? <ErrorState error={sla.error} onRetry={() => void sla.refetch()} /> : null}
      {sla.data
        ? SLA_FIELDS.map((f) => (
            <Field
              key={f.key}
              label={t(f.label)}
              name={f.key}
              type="number"
              value={draft[f.key] ?? ''}
              onChange={(v) => setDraft((d) => ({ ...d, [f.key]: v }))}
              ltr
            />
          ))
        : null}
      {save.error ? <ErrorState error={save.error} /> : null}
      <button className="btn" type="button" disabled={save.isPending || version === null} onClick={run}>
        {t('action.save')}
      </button>
    </Card>
  );
}
